import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { Network } from '@capacitor/network';

/**
 * ConnectivityContext — Uses Capacitor Network plugin to detect
 * online/offline state and connection type across web + native.
 *
 * Provides:
 *   isConnected  — boolean
 *   connectionType — 'wifi' | 'cellular' | 'none' | 'unknown'
 *   isWifi       — boolean (convenience)
 *   isCellular   — boolean (convenience)
 */

const ConnectivityContext = createContext(null);

export const useConnectivity = () => {
  const ctx = useContext(ConnectivityContext);
  if (!ctx) throw new Error('useConnectivity must be used within ConnectivityProvider');
  return ctx;
};

export const ConnectivityProvider = ({ children }) => {
  const [isConnected, setIsConnected] = useState(navigator.onLine);
  const [connectionType, setConnectionType] = useState('unknown');
  const listenerHandle = useRef(null);

  // Derive convenience booleans
  const isWifi = connectionType === 'wifi';
  const isCellular = connectionType === 'cellular';

  const applyStatus = useCallback((status) => {
    setIsConnected(status.connected);
    setConnectionType(status.connectionType || 'unknown');
  }, []);

  useEffect(() => {
    let cancelled = false;

    // 1. Read initial status
    Network.getStatus()
      .then((status) => { if (!cancelled) applyStatus(status); })
      .catch(() => {
        // Fallback if Capacitor plugin unavailable (plain browser)
        if (!cancelled) {
          setIsConnected(navigator.onLine);
          setConnectionType(navigator.onLine ? 'wifi' : 'none');
        }
      });

    // 2. Listen for changes via Capacitor
    Network.addListener('networkStatusChange', (status) => {
      if (!cancelled) applyStatus(status);
    }).then((handle) => {
      listenerHandle.current = handle;
    }).catch(() => {
      // Capacitor native layer not available — fall back to browser events
      const onOnline = () => { setIsConnected(true); setConnectionType('wifi'); };
      const onOffline = () => { setIsConnected(false); setConnectionType('none'); };
      window.addEventListener('online', onOnline);
      window.addEventListener('offline', onOffline);
      listenerHandle.current = { remove: () => {
        window.removeEventListener('online', onOnline);
        window.removeEventListener('offline', onOffline);
      }};
    });

    return () => {
      cancelled = true;
      if (listenerHandle.current?.remove) listenerHandle.current.remove();
    };
  }, [applyStatus]);

  return (
    <ConnectivityContext.Provider value={{ isConnected, connectionType, isWifi, isCellular }}>
      {children}
    </ConnectivityContext.Provider>
  );
};
